import type { startseite, wahl } from "@progressive-development/politik-model";

/**
 * Welche Teilwahl gilt — die eine Auswahllogik für alle Wahl-Anzeigen.
 *
 * Hier steht, WELCHES Ergebnis gezeigt wird (`teilwahl`, `juengsteTeilwahlen`)
 * und die eine Arithmetik, die jede Anzeige braucht (`anteil`). Was daraus
 * für die Anzeige gebildet wird (Kurzfassung, Klammerwerte, Zeitvergleich),
 * steht in `wahl-kurzfassung.ts`.
 *
 * Ein Anteil aus amtlichen Stimmen und amtlichen gültigen Stimmen ist
 * Arithmetik, kein Aggregat: Beide Zahlen stehen nebeneinander in derselben
 * Quelle; sie zu teilen erfindet nichts.
 */

/**
 * Anteil an den gültigen Stimmen, in Prozent.
 *
 * Ohne gültige Stimmen gibt es keinen Anteil — dann `undefined` und nicht 0.
 */
export function anteil(
  stimmen: number | undefined,
  gueltigeStimmen: number | undefined,
): number | undefined {
  if (!gueltigeStimmen || gueltigeStimmen <= 0 || stimmen === undefined) return undefined;
  return (stimmen / gueltigeStimmen) * 100;
}

/**
 * Die AKTUELLE Teilwahl einer Art aus einem Gebiets-Eintrag.
 *
 * Über den kuratierten `art`-Slug, nicht über den Wortlaut: Der trägt bei
 * Bezirksvertretungswahlen den Bezirksnamen mit ("Wahl der Bezirksvertretung
 * (Ehrenfeld)") und ändert sich zwischen Wahlen — dasselbe Amt hieß 2020
 * "Oberbürgermeisterwahl" und 2025 "Wahl des*der Oberbürgermeister*in".
 *
 * WELCHE der Wahlen die aktuelle ist, sagt die PIPELINE und nicht diese
 * Funktion: `teilwahlId` am Eintrag gegen `aktuell.jeArt` bzw.
 * `aktuell.jeBezirk`. Die Regel dahinter (jüngster Wahltag je Art) hier
 * nachzubauen hieße, sie zweimal zu haben — und der Nachbau wäre falsch,
 * sobald eine Teilwahl einmal fehlt: Gibt es zu einer Wahl keine Stichwahl,
 * zeigte "die jüngste ihrer Art" die Stichwahl der VORIGEN Wahl als aktuelle.
 *
 * OHNE die Angabe greift die zugesagte Reihenfolge des Verzeichnisses
 * (neueste zuerst, Wahltag absteigend) — deshalb der erste Treffer und keine
 * eigene Sortierung. Die Zusage steht am `GebietEintrag` im Modell.
 */
export function teilwahl(
  gebiet: startseite.GebietEintrag | undefined,
  art: string,
  aktuell?: wahl.AktuelleWahlen,
): startseite.Wahlzusammenfassung | undefined {
  const passende = (gebiet?.wahl ?? []).filter((w) => w.art === art);

  // Ohne die Angabe greift die ZUSAGE des Verzeichnisses: neueste zuerst.
  if (!aktuell) return passende[0];

  // Bezirksvertretungswahlen stehen bewusst nicht in jeArt — es gibt neun.
  const bezirk = gebiet?.bezirkSlug ?? gebiet?.slug ?? "";
  const gesucht = aktuell.jeArt?.[art] ?? aktuell.jeBezirk?.[bezirk];

  /*
   * STRENG: Ohne Treffer NICHTS, nicht ersatzweise die vorige Wahl.
   *
   * Gibt es zu dieser Art gerade keine Teilwahl — etwa weil eine Wahl ohne
   * Stichwahl entschieden wurde —, dann waere die Stichwahl der VORIGEN Wahl
   * als aktuelle das Gegenteil einer Auskunft. Im Bestand tragen alle 594
   * Eintraege ihre teilwahlId, ein Treffer faellt also nicht versehentlich aus.
   */
  return gesucht ? passende.find((w) => w.teilwahlId === gesucht) : undefined;
}

/**
 * Je Art die aktuelle Teilwahl — für Listen, die alle Arten aufzählen.
 *
 * Ohne diese Bündelung stünde seit der zweiten Kommunalwahl jede Teilwahl
 * doppelt in der Liste.
 */
export function juengsteTeilwahlen(
  gebiet: startseite.GebietEintrag | undefined,
  aktuell?: wahl.AktuelleWahlen,
): startseite.Wahlzusammenfassung[] {
  const arten = [...new Set((gebiet?.wahl ?? []).map((w) => w.art).filter(Boolean))];
  return arten
    .map((art) => teilwahl(gebiet, art as string, aktuell))
    .filter((w): w is startseite.Wahlzusammenfassung => w !== undefined);
}
